import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { biz, type SalesDoc } from '../../lib/business'
import { money } from '../../lib/format'
import { useToast } from '../../app/Toasts'
import { Field } from './ContactsPage'

interface PaymentRow {
  id: string
  direction: string
  amount: number
  method: string
  reference: string | null
  paid_on: string | null
  contact_name: string | null
  allocated: number
  unallocated: number
}

export default function PaymentsPage() {
  const { push } = useToast()
  const [rows, setRows] = useState<PaymentRow[]>([])
  const [outstanding, setOutstanding] = useState<SalesDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [recording, setRecording] = useState(false)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    invoiceId: '', amount: 0, method: 'bank', reference: '',
  })

  const load = useCallback(async () => {
    try {
      const [pays, invs] = await Promise.all([
        biz.payments(), biz.salesDocs('invoices'),
      ])
      setRows(pays.items)
      setOutstanding(invs.items.filter(
        (d) => d.balance_due > 0.005 && !['draft', 'cancelled'].includes(d.status)))
    } catch { /* ignore */ } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const chosen = outstanding.find((d) => d.id === form.invoiceId)

  const record = async () => {
    if (!chosen) return
    setBusy(true)
    try {
      await biz.recordPayment({
        direction: 'in', amount: form.amount, contact_id: chosen.contact_id,
        method: form.method, reference: form.reference || null,
        allocations: [{
          target_type: 'sales_doc', target_id: chosen.id, amount: form.amount,
        }],
      })
      push('success', 'Payment recorded',
        `${money(form.amount)} against ${chosen.number}`)
      setRecording(false)
      setForm({ invoiceId: '', amount: 0, method: 'bank', reference: '' })
      load()
    } catch (e) {
      push('error', 'Could not record the payment',
        e instanceof ApiError ? e.message : undefined)
    } finally { setBusy(false) }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">Awaiting payment</span>
          <div className="grow" />
          <span className="muted" style={{ fontSize: 12.5 }}>
            {outstanding.length} invoice{outstanding.length === 1 ? '' : 's'} ·{' '}
            <strong style={{ color: 'var(--warn)' }}>
              {money(outstanding.reduce((s, d) => s + d.balance_due, 0))}
            </strong>
          </span>
          <button className="btn btn-sm btn-primary"
            onClick={() => setRecording(true)} disabled={outstanding.length === 0}>
            Record payment
          </button>
        </div>
        {outstanding.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Nothing outstanding</div>
            <div>Every sent invoice has been paid in full.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Invoice</th><th>Customer</th><th>Due</th>
                  <th className="num">Total</th><th className="num">Paid</th>
                  <th className="num">Balance</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {outstanding.map((d) => (
                  <tr key={d.id}>
                    <td className="mono" style={{ fontSize: 12 }}>{d.number}</td>
                    <td>{d.contact?.name}</td>
                    <td>{d.due_date}</td>
                    <td className="num">{money(d.total)}</td>
                    <td className="num">{money(d.amount_paid)}</td>
                    <td className="num" style={{ fontWeight: 560, color: 'var(--warn)' }}>
                      {money(d.balance_due)}
                    </td>
                    <td>
                      <span className={`pill ${d.payment_status === 'partial' ? 'pill-warn' : 'pill-mute'}`}>
                        {d.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {recording && (
        <div className="card">
          <div className="card-head"><span className="card-title">Record a payment</span></div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Invoice" required>
                <select className="input" value={form.invoiceId}
                  onChange={(e) => {
                    const inv = outstanding.find((d) => d.id === e.target.value)
                    setForm({
                      ...form, invoiceId: e.target.value,
                      amount: inv ? inv.balance_due : 0,
                    })
                  }}>
                  <option value="">Select an invoice…</option>
                  {outstanding.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.number} — {d.contact?.name} — {money(d.balance_due)} due
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Amount" required
                hint={chosen ? `Balance due ${money(chosen.balance_due)}` : undefined}>
                <input className="input" type="number" step="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />
              </Field>
              <Field label="Method">
                <select className="input" value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value })}>
                  {['bank', 'upi', 'cash', 'cheque', 'card'].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Field>
              <Field label="Reference" hint="UTR, cheque number, transaction id">
                <input className="input" value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </Field>
            </div>
            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={record}
                disabled={busy || !chosen || form.amount <= 0}>
                {busy && <span className="spinner" />}Record
              </button>
              <button className="btn" onClick={() => setRecording(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head"><span className="card-title">Payment history</span></div>
        {loading ? (
          <div className="card-body"><div className="skeleton" style={{ height: 100 }} /></div>
        ) : rows.length === 0 ? (
          <div className="empty"><div className="empty-title">No payments yet</div></div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Date</th><th>Direction</th><th>Party</th><th>Method</th>
                  <th>Reference</th><th className="num">Amount</th>
                  <th className="num">Unapplied</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td>{p.paid_on}</td>
                    <td>
                      <span className={`pill ${p.direction === 'in' ? 'pill-ok' : 'pill-warn'}`}>
                        {p.direction === 'in' ? 'received' : 'paid out'}
                      </span>
                    </td>
                    <td>{p.contact_name ?? '—'}</td>
                    <td>{p.method}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{p.reference ?? '—'}</td>
                    <td className="num" style={{ fontWeight: 560 }}>{money(p.amount)}</td>
                    <td className="num" style={{ color: p.unallocated > 0 ? 'var(--warn)' : undefined }}>
                      {money(p.unallocated)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
